import { useTranslation } from "@redotech/redo-i18n";
import { memo, useState } from "react";
import type { RedoListItem } from "./arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "./arbiter-components/select-dropdown/redo-single-select-dropdown-input";

export interface ProductVariantInfo {
  variantId: string;
  name: string;
  imageSrc: string;
}

export interface ProductState {
  loading: boolean;
  options: ProductVariantInfo[];
  onProductsInputChange: (event: any, value: string) => void;
}

function productToListItem(
  product: ProductVariantInfo,
): RedoListItem<ProductVariantInfo> {
  return {
    id: product.variantId,
    type: "text",
    text: product.name,
    value: product,
    leadingItem: product.imageSrc
      ? {
          type: "avatar",
          imageUrl: product.imageSrc,
          alt: product.name,
          name: product.name,
        }
      : undefined,
  };
}

export const ProductAutocomplete = memo(function ProductAutocomplete({
  label,
  value,
  setValue,
  productsState,
  disabled,
}: {
  label: string;
  value: ProductVariantInfo | null | undefined;
  setValue: (value: ProductVariantInfo | null | undefined) => void;
  productsState: ProductState;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [searchString, setSearchString] = useState("");

  const handleSearchString = (search: string) => {
    setSearchString(search);
    productsState.onProductsInputChange(undefined, search);
  };

  return (
    <RedoSingleSelectDropdownInput
      disabled={disabled}
      emptyListMessage={
        searchString
          ? productsState.loading
            ? t("Loading...")
            : t("No options")
          : t("Type to search")
      }
      fullWidth
      itemsLoading={productsState.loading}
      label={label || undefined}
      options={(productsState.options || []).map(productToListItem)}
      optionSelected={({ item }) => {
        setValue(item.value ?? null);
      }}
      placeholder={t("Search products")}
      searchPlaceholder={t("Search products")}
      searchString={searchString}
      selectedItem={value ? productToListItem(value) : undefined}
      setSearchString={handleSearchString}
      size="xs"
      uiSelector="product-autocomplete"
    />
  );
});
